import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { saveArticleToDirectus } from './saveArticle';
import debounce from 'lodash/debounce';
import FullScreenLoader from './GenerateDraft/FullScreenLoader';
import TopBar from '../../layouts/TopBar/topBar';
import useEditor from './useEditor';
import { useTranslation } from 'react-i18next';
import { MAX_KEYWORDS, MAX_SOURCES, toggleSourceSelection } from '../../features/articles/lib/sources';
import { buildKeywordContextFromDraft, extractPlainTextFromEditorBlocks } from '../../features/articles/lib/editorBlocks';
import { isLocalhost } from '../../features/articles/lib/workerEndpoints';
import {
  CONTENT_TYPES,
  buildStepUrl,
  buildWizardSteps,
  getNextStep,
  getPreviousStep,
  isChangelogType,
  normalizeStepForContentType,
  resolveStepNumber,
} from '../../features/articles/lib/wizardSteps';
import { formatTimeAgo } from '../../features/articles/lib/formatTimeAgo';
import { log } from '../../features/articles/services/articleGenerationApi';
import { useArticleGeneration } from '../../features/articles/hooks/useArticleGeneration';
import { useGenerationLoader } from '../../features/articles/hooks/useGenerationLoader';
import { useWizardReferenceData } from '../../features/articles/hooks/useWizardReferenceData';
import { useWizardArticleLoader } from '../../features/articles/hooks/useWizardArticleLoader';
import WizardStepper from '../../features/articles/components/create-article/WizardStepper';
import SetupStep from '../../features/articles/components/create-article/SetupStep';
import ContentStep from '../../features/articles/components/create-article/ContentStep';
import SourcesStep from '../../features/articles/components/create-article/SourcesStep';
import KeywordsStep from '../../features/articles/components/create-article/KeywordsStep';
import BulkReviewStep from '../../features/articles/components/create-article/BulkReviewStep';
import ReviewSummary from '../../features/articles/components/create-article/ReviewSummary';
import WizardFooter from '../../features/articles/components/create-article/WizardFooter';
import GeneratingOverlay from '../../features/articles/components/create-article/GeneratingOverlay';
import GenerationSuccessOverlay from '../../features/articles/components/create-article/GenerationSuccessOverlay';

// Composition root of the "create article" wizard. Pure logic lives in
// features/articles/lib, generation flows in features/articles/hooks and the step
// sections in features/articles/components/create-article.
export default function CreateArticleDraft() {
  const { t } = useTranslation();
  // Get step from URL params and search params for article ID
  const { stepNumber } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to validate and get step number (DEFAULT to step 1 - setup)
  const getValidStepNumber = () => resolveStepNumber(stepNumber);

  // Basic states
  const [currentStep, setCurrentStep] = useState(getValidStepNumber());
  const [articleType] = useState('single');
  // Bulk articles settings
  const [numberOfArticles, setNumberOfArticles] = useState(5);
  const [bulkTopic, setBulkTopic] = useState('');
  const [bulkTopicError, setBulkTopicError] = useState('');
  const [contentType, setContentType] = useState('blogpost'); // Default to Blog Post
  const [articleContext, setArticleContext] = useState('');
  const [articleContextError, setArticleContextError] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [sources, setSources] = useState([]);
  const [selectedArticleForSummary, setSelectedArticleForSummary] = useState(null);

  // Article structure selected from Directus (list comes from useWizardReferenceData)
  const [selectedStructure, setSelectedStructure] = useState(null);

  // Knowledge selection mode for summary content type
  const [knowledgeMode, setKnowledgeMode] = useState('manual'); // 'manual' or 'automatic'
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [toneOfVoice, setToneOfVoice] = useState('casual');
  const [complexity, setComplexity] = useState('fog index: 15');
  const [language, setLanguage] = useState('polski');
  const [includeImages, setIncludeImages] = useState(true);
  const [thumbnailSource, setThumbnailSource] = useState('pexels'); // 'gemini' or 'pexels' (default: pexels)
  const [selectedProject, setSelectedProject] = useState('');
  const [editorHasContent, setEditorHasContent] = useState(false);
  const [, setUserHasEditedEditor] = useState(false);
  const [isCreatingArticle, setIsCreatingArticle] = useState(false);
  const {
    isFullScreenLoading,
    setIsFullScreenLoading,
    loaderType,
    setLoaderType,
    loaderDetails,
    setLoaderDetails,
    updateLoaderProgress,
  } = useGenerationLoader();
  // Editor and article states
  const [, setShowEditor] = useState(false);
  const [generatedArticle] = useState('');
  const [fullPageView] = useState(false);
  const [generatedArticleId, setGeneratedArticleId] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const articleDataRef = useRef(null);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const isAutosavingRef = useRef(false);

  // Sync articleData with ref
  useEffect(() => {
    articleDataRef.current = articleData;
  }, [articleData]);
  const [lastSaved, setLastSaved] = useState('');
  const [forceEditorRefresh, setForceEditorRefresh] = useState(false);
  const [isDraftGenerated, setIsDraftGenerated] = useState(false);
  const [triggerSourcesSearch, setTriggerSourcesSearch] = useState(0);
  const [sourceSearchContext, setSourceSearchContext] = useState('');
  const [serpKeywordsVisible, setSerpKeywordsVisible] = useState(false);
  const [triggerSerpSearch, setTriggerSerpSearch] = useState(0);
  const [keywordSearchContext, setKeywordSearchContext] = useState('');
  const [isGenerationCompleted, setIsGenerationCompleted] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [, setHasInitialArticle] = useState(false);

  const navigate = useNavigate();
  const editorRef = useRef(null);
  const sourcesSearchTriggeredRef = useRef(false);
  const editorElementRef = useRef(null);
  const articleContextRef = useRef('');
  const previousStepRef = useRef(getValidStepNumber());
  const isCreatingRef = useRef(false);

  useEffect(() => {
    articleContextRef.current = articleContext || '';
  }, [articleContext]);

  // URL-aware step navigation functions
  const navigateToStep = (requestedStep, typeOverride) => {
    // A changelog has no sources/keywords steps; resuming onto one lands on review.
    const step = normalizeStepForContentType(requestedStep, typeOverride || contentType);
    const articleId = searchParams.get('id') || generatedArticleId;

    // Clear validation errors when navigating between steps
    setArticleContextError('');
    setBulkTopicError('');

    // Build URL with params
    const url = buildStepUrl(step, { articleId, contentType: typeOverride || contentType });
    navigate(url, { replace: true });
    setCurrentStep(step);

    // Prevent transient focus/selection artifacts in the step header.
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    // Scroll to top without smooth animation to avoid UI flash between steps.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  // Dynamic page title and description based on current step (SKIPPING 'type' step)
  const { pageTitle, pageDescription } = useMemo(() => {
    const stepNames = {
      1: 'setup',
      2: 'content',
      3: 'sources',
      4: 'keywords',
      5: 'review'
    };
    const currentStepName = stepNames[currentStep] || 'setup';

    return {
      pageTitle: t(`articles.create.steps.${currentStepName}.title`),
      pageDescription: t(`articles.create.steps.${currentStepName}.pageDescription`)
    };
  }, [currentStep, t]);

  const handleSave = useCallback(async (isAutosave = false) => {
    if (editorRef.current && editorRef.current.save) {
      // Get current article ID from URL or state
      const currentArticleId = searchParams.get('id') || generatedArticleId;

      if (!currentArticleId) {
        log('No article ID available for save operation');
        return;
      }

      // Prevent concurrent saves
      if (isAutosaving) {
        log('Save already in progress, skipping');
        return;
      }

      try {
        setIsAutosaving(true);
        isAutosavingRef.current = true;
        const outputData = await editorRef.current.save();

        const articleDataToSave = {
          id: currentArticleId, // Always use existing article ID
          title: outputData.blocks[0]?.data?.text || 'Untitled',
          article_draft: JSON.stringify(outputData),
          keywords: keywords,
          content_type: contentType,
          tone_of_voice: toneOfVoice,
          fog_index: complexity,
          language: language,
          thread_id: articleDataRef.current?.thread_id || null,
          project: selectedProject || null,
          article_linked_structure: selectedStructure || null,
          article_thumbnail_included: includeImages,
          article_thumbnail_source: includeImages ? thumbnailSource : null,
          sources: sources,
          article_context: articleContext,
        };

        console.log('💾 handleSave: selectedStructure =', selectedStructure, 'articleDataToSave:', articleDataToSave);

        const savedArticle = await saveArticleToDirectus(articleDataToSave);
        log('Saved article:', savedArticle);
        log('🔍 CREATE_ARTICLE DEBUG: Saved article response:', savedArticle);
        log('🔍 CREATE_ARTICLE DEBUG: Saved article data:', savedArticle.data);

        // Ensure state is synchronized
        if (!generatedArticleId && savedArticle.data.id) {
          setGeneratedArticleId(savedArticle.data.id);
        }

        // Update UI state after save
        setLastSaved(new Date().toISOString());
        setSaveError('');

        // Only update full state for manual saves, not autosaves
        if (!isAutosave) {
          setArticleData(savedArticle.data);
          setEditorHasContent(true);
          setIsDraftGenerated(true);
        }

        log('Article draft ' + (isAutosave ? 'auto' : '') + 'saved successfully');
      } catch (error) {
        setSaveError('Error during saving: ' + error.message);
      } finally {
        setIsAutosaving(false);
        isAutosavingRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isAutosaving omitted; guarded inside callback body
  }, [generatedArticleId, keywords, contentType, toneOfVoice, complexity, language, selectedProject, selectedStructure, includeImages, thumbnailSource, sources, articleContext, searchParams]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce() wrapper; deps listed manually below
  const debouncedSave = useCallback(
    debounce(() => {
      // Only save if we have an article ID and no save is in progress
      const currentArticleId = searchParams.get('id') || generatedArticleId;
      if (currentArticleId && !isAutosavingRef.current) {
        handleSave(true);
      }
    }, 2000),
    [handleSave, generatedArticleId, searchParams]
  );

  useEditor(
    editorRef,
    useRef(null),
    debouncedSave,
    {
      articleData,
      setEditorHasContent,
      setUserHasEditedEditor,
      generatedArticleId,
      isLocalhost,
      isAutosaving,
      shouldInitialize: articleType === 'single' && currentStep === 2,
      editorMountKey: `${articleType}-${currentStep}-${articleData?.id || 'new'}`,
      forceRefresh: forceEditorRefresh,
      paragraphPlaceholder: isChangelogType(contentType)
        ? t('articles.create.changelogContext.placeholder')
        : t('articles.create.contentContextPlaceholder'),
      onEditorChange: (outputData) => {
        if (isDraftGenerated) return;
        const plainText = extractPlainTextFromEditorBlocks(outputData?.blocks || []);
        articleContextRef.current = plainText;
        setArticleContext(plainText);
        if (plainText) {
          setArticleContextError('');
        }
      }
    }
  );

  const syncArticleContextFromEditor = useCallback(async () => {
    if (!editorRef.current || !editorRef.current.save) {
      return (articleContextRef.current || '').trim();
    }

    try {
      const outputData = await editorRef.current.save();
      const plainText = extractPlainTextFromEditorBlocks(outputData?.blocks || []);
      articleContextRef.current = plainText;
      setArticleContext(plainText);
      return plainText;
    } catch (error) {
      log('Failed to read content from EditorJS for article_context:', error?.message);
      return (articleContextRef.current || '').trim();
    }
  }, []);

  const runEditorCommand = useCallback((command, value = null) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    try {
      document.execCommand(command, false, value);
    } catch (error) {
      log(`Editor command failed: ${command}`, error?.message);
    }
  }, []);

  const convertCurrentBlock = useCallback((target) => {
    switch (target) {
      case 'paragraph':
        runEditorCommand('formatBlock', 'P');
        break;
      case 'heading2':
        runEditorCommand('formatBlock', 'H2');
        break;
      case 'heading3':
        runEditorCommand('formatBlock', 'H3');
        break;
      case 'unordered':
        runEditorCommand('insertUnorderedList');
        break;
      case 'ordered':
        runEditorCommand('insertOrderedList');
        break;
      default:
        break;
    }
  }, [runEditorCommand]);

  const insertEditorBlock = useCallback((type, data = {}) => {
    if (!editorRef.current?.blocks) return;

    try {
      const currentIndex = typeof editorRef.current.blocks.getCurrentBlockIndex === 'function'
        ? editorRef.current.blocks.getCurrentBlockIndex()
        : -1;
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : undefined;

      editorRef.current.blocks.insert(type, data, undefined, insertIndex, true);
    } catch (error) {
      log(`Insert block failed: ${type}`, error?.message);
    }
  }, []);

  useEffect(() => {
    const previousStep = previousStepRef.current;
    const enteredKeywordsStep = previousStep !== 4 && currentStep === 4;

    if (enteredKeywordsStep && articleType === 'single') {
      const fromDraft = buildKeywordContextFromDraft(articleData?.article_draft);
      const fromEditor = (articleContextRef.current || '').trim();
      const fallback = (articleContext || '').trim();
      const preparedContext = fromDraft || fromEditor || fallback;

      if (preparedContext.length > 3) {
        setKeywordSearchContext(preparedContext);
        setSerpKeywordsVisible(true);
        setTriggerSerpSearch(Date.now());
        log('🔎 KEYWORDS: Auto search triggered on step entry', {
          contextLength: preparedContext.length,
          source: fromDraft ? 'draft' : fromEditor ? 'editor' : 'fallback'
        });
      }
    }

    previousStepRef.current = currentStep;
  }, [currentStep, articleType, articleData?.article_draft, articleContext]);

  useEffect(() => {
    if (currentStep !== 2 || articleType !== 'single' || contentType === 'summary' || isChangelogType(contentType)) return;
    if (!isDraftGenerated || sourcesSearchTriggeredRef.current) return;

    const fromDraft = buildKeywordContextFromDraft(articleData?.article_draft);
    const fromEditor = (articleContextRef.current || '').trim();
    const fallback = (articleContext || '').trim();
    const preparedContext = fromDraft || fromEditor || fallback;

    if (preparedContext.length > 3) {
      sourcesSearchTriggeredRef.current = true;
      setSourceSearchContext(preparedContext);
      setTriggerSourcesSearch(Date.now());
      log('🔎 SOURCES: Auto search triggered after draft', {
        contextLength: preparedContext.length
      });
    }
  }, [currentStep, articleType, contentType, isDraftGenerated, articleData?.article_draft, articleContext]);

  const {
    handleContinue,
    handleSubmit,
  } = useArticleGeneration({
    t,
    navigate,
    searchParams,
    getValidStepNumber,
    editorRef,
    articleContextRef,
    sourcesSearchTriggeredRef,
    syncArticleContextFromEditor,
    articleType,
    currentStep,
    setCurrentStep,
    loading,
    setLoading,
    numberOfArticles,
    bulkTopic,
    setBulkTopicError,
    articleContext,
    setArticleContext,
    keywords,
    sources,
    contentType,
    toneOfVoice,
    complexity,
    language,
    selectedProject,
    selectedStructure,
    includeImages,
    thumbnailSource,
    knowledgeMode,
    selectedArticleForSummary,
    generatedArticleId,
    setGeneratedArticleId,
    articleData,
    setArticleData,
    setHasInitialArticle,
    editorHasContent,
    setEditorHasContent,
    setShowEditor,
    setForceEditorRefresh,
    setLastSaved,
    setSaveError,
    setIsDraftGenerated,
    isGenerationCompleted,
    setIsGenerationCompleted,
    setIsGenerating,
    setIsFullScreenLoading,
    setLoaderType,
    setLoaderDetails,
    updateLoaderProgress,
  });

  const handleAddKeyword = () => {
    const normalizedKeyword = keywordInput.trim();
    if (!normalizedKeyword || keywords.includes(normalizedKeyword)) {
      return;
    }
    if (keywords.length >= MAX_KEYWORDS) {
      return;
    }
    if (normalizedKeyword) {
      setKeywords([...keywords, normalizedKeyword]);
      setKeywordInput('');
      // Auto-save when keywords are added
    // setTimeout(() => debouncedEarlySave(), 100); // DISABLED: No autosave during form filling
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter((k) => k !== keyword));
    // Auto-save when keywords are removed
    // setTimeout(() => debouncedEarlySave(), 100); // DISABLED: No autosave during form filling
  };

  // Summary-specific article selection
  const handleSelectedArticleForSummary = (article) => {
    log('🔄 SUMMARY: Setting selected article:', article);
    setSelectedArticleForSummary(article);
    // Auto-save when article is selected
    // setTimeout(() => debouncedEarlySave(), 100); // DISABLED: No autosave during form filling
  };

  const handleRemoveSelectedArticleForSummary = () => {
    log('Removing selected article for summary');
    setSelectedArticleForSummary(null);
    // Auto-save when article is removed
    // setTimeout(() => debouncedEarlySave(), 100); // DISABLED: No autosave during form filling
  };

  const handleToggleArticleSource = (source, shouldSelect) => {
    setSources((prev) => toggleSourceSelection(prev, source, shouldSelect, MAX_SOURCES));
  };

  // Update steps array (SKIPPING 'type' step)
  const steps = buildWizardSteps(t, currentStep, contentType);

  const goToNextStep = () => {
    // STEP 0 REMOVED - Start from Step 1 (Setup)
    if (currentStep === 1) {
      // Validate Setup step
      if (!contentType) {
        return;
      }
    } else if (currentStep === 2 && articleType === 'single') {
      // Validate Content step - skip validation for summary (uses source selection instead)
      if (contentType !== 'summary') {
        if (!articleContext || articleContext.trim() === '') {
          setArticleContextError(t('articles.create.validation.contextRequired'));
          return;
        }
        setArticleContextError('');
      }
      if (!isDraftGenerated) {
        setSaveError(t('articles.create.validation.generateDraftFirst'));
        return;
      }
    }

    const nextStep = getNextStep(currentStep, contentType);
    if (nextStep) {
      navigateToStep(nextStep);
    }
  };

  const goToPreviousStep = () => {
    const previousStep = getPreviousStep(currentStep, contentType);
    if (previousStep) {
      navigateToStep(previousStep);
    }
  };

  // Handle URL parameter changes
  useEffect(() => {
    // Read contentType from URL if present
    const urlContentType = searchParams.get('type');
    const effectiveContentType = CONTENT_TYPES.includes(urlContentType) ? urlContentType : contentType;
    if (urlContentType && urlContentType !== contentType && CONTENT_TYPES.includes(urlContentType)) {
      setContentType(urlContentType);
      log('📖 Loaded contentType from URL:', urlContentType);
    }

    const newStep = getValidStepNumber();
    const allowedStep = normalizeStepForContentType(newStep, effectiveContentType);
    if (stepNumber && allowedStep !== newStep) {
      navigateToStep(allowedStep, effectiveContentType);
    } else if (newStep !== currentStep) {
      setCurrentStep(newStep);
    }

    // If no step in URL, redirect to first step (SETUP - step 0 removed)
    if (!stepNumber) {
      const articleId = searchParams.get('id');
      const typeParam = contentType ? `&type=${contentType}` : '';
      const url = articleId
        ? `/writer/articles/create-article/step/setup?id=${articleId}${typeParam}`
        : `/writer/articles/create-article/step/setup${typeParam ? '?' + typeParam.substring(1) : ''}`;
      navigate(url, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync step/type from URL only; full deps cause redirect loops
  }, [stepNumber, searchParams]);

  // Projects + published article structures for the workspace
  const { availableStructures } = useWizardReferenceData();

  // Handler for content type change with smart thumbnail source defaults
  const handleContentTypeChange = useCallback((newContentType) => {
    const oldContentType = contentType;
    setContentType(newContentType);

    // Clear any validation errors when changing content type
    setArticleContextError('');

    // Update URL with new content type
    const params = new URLSearchParams(searchParams);
    params.set('type', newContentType);
    setSearchParams(params, { replace: true });
    log('🔗 Updated URL with contentType:', newContentType);

    // Auto-adjust thumbnail source ONLY when switching between blog/summary
    // and user hasn't manually changed from the expected default for that type
    if (oldContentType !== newContentType) {
      if (newContentType === 'summary' && thumbnailSource === 'pexels') {
        // Switching to summary from blog (which has pexels default) -> switch to AI
        setThumbnailSource('gemini');
        log('🎨 Auto-switched to AI Generated for Summary (was on Stock Images)');
      } else if (oldContentType === 'summary' && newContentType !== 'summary' && thumbnailSource === 'gemini') {
        // Switching from summary to blog (and user had AI) -> switch to Stock
        setThumbnailSource('pexels');
        log('🎨 Auto-switched to Stock Images for Blog Post (was on AI Generated)');
      }
      // If user manually changed thumbnail source, respect their choice
    }
  }, [contentType, thumbnailSource, searchParams, setSearchParams]);

  // Recent draft auto-load + loading an existing article from ?id=
  useWizardArticleLoader({
    navigate,
    searchParams,
    getValidStepNumber,
    navigateToStep,
    isCreatingRef,
    isCreatingArticle,
    setIsCreatingArticle,
    isAutosaving,
    contentType,
    articleContext,
    generatedArticleId,
    setGeneratedArticleId,
    setArticleData,
    setHasInitialArticle,
    setContentType,
    setArticleContext,
    setKeywords,
    setSources,
    setToneOfVoice,
    setComplexity,
    setLanguage,
    setSelectedProject,
    setIncludeImages,
    setThumbnailSource,
    setSelectedStructure,
    setLastSaved,
    setIsDraftGenerated,
    setEditorHasContent,
    setShowEditor,
    setSaveError,
  });

  // Funkcje do nawigacji
  const goToArticles = () => {
    // Reset modal states before navigation
    setIsGenerationCompleted(false);
    setIsGenerating(false);
    navigate('/writer/articles');
  };

  // Format date function for TopBar
  const formatDateUpdated = (date) => formatTimeAgo(t, date);

  // Go back function for TopBar
  const goBack = () => {
    navigate('/writer/articles');
  };

  const toneOfVoiceOptions = useMemo(() => ([
    { value: 'friendly', label: t('articles.create.options.tone.friendly') },
    { value: 'professional', label: t('articles.create.options.tone.professional') },
    { value: 'casual', label: t('articles.create.options.tone.casual') },
    { value: 'formal', label: t('articles.create.options.tone.formal') },
    { value: 'authoritative', label: t('articles.create.options.tone.authoritative') },
  ]), [t]);
  const complexityOptions = useMemo(() => ([
    { value: 'fog index: 5', label: t('articles.create.options.complexity.verySimple') },
    { value: 'fog index: 10', label: t('articles.create.options.complexity.simple') },
    { value: 'fog index: 15', label: t('articles.create.options.complexity.moderate') },
    { value: 'fog index: 20', label: t('articles.create.options.complexity.complex') },
    { value: 'fog index: 25', label: t('articles.create.options.complexity.professional') },
  ]), [t]);
  const languageOptions = useMemo(() => ([
    { value: 'polski', label: t('articles.create.options.language.polish') },
    { value: 'english', label: t('articles.create.options.language.english') },
    { value: 'german', label: t('articles.create.options.language.german') },
  ]), [t]);

  const effectiveKeywordContext = (keywordSearchContext || articleContext || '').trim();
  const selectedStructureLabel = availableStructures.find((item) => item.id === selectedStructure)?.title || t('articles.create.articleStructure.defaultOption');
  const selectedToneLabel = toneOfVoiceOptions.find((opt) => opt.value === toneOfVoice)?.label || toneOfVoice;
  const selectedLanguageLabel = languageOptions.find((opt) => opt.value === language)?.label || language;
  const complexityLabelFromOption = complexityOptions.find((opt) => opt.value === complexity)?.label;
  const complexityNumber = String(complexity || '').match(/\d+/)?.[0];
  const complexityLabelFromNumber = complexityNumber
    ? complexityOptions.find((opt) => opt.value.includes(`fog index: ${complexityNumber}`))?.label
    : null;
  const selectedComplexityLabel = complexityLabelFromOption || complexityLabelFromNumber || complexity;
  const includeImagesLabel = includeImages ? t('common.yes') : t('common.no');

  if (fullPageView) {
    return (
      <div className="min-h-screen bg-neutral-50 p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-8">{articleData?.title || 'Generated Article'}</h1>

          {/* Wyświetl zawartość bez konwersji marked - użyj dangerouslySetInnerHTML, ale z surowym HTML/tekstem */}
          <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: generatedArticle }} />

        </div>
      </div>
    );
  }

  if (isFullScreenLoading) {
    return (
      <FullScreenLoader
        type={loaderType}
        phase={loaderDetails.phase}
        detail={loaderDetails.detail}
        backend={loaderDetails.backend}
        articleId={loaderDetails.articleId}
        queueStatus={loaderDetails.queueStatus}
        attempt={loaderDetails.attempt}
        totalAttempts={loaderDetails.totalAttempts}
      />
    );
  }

  const handleCreateAnother = () => {
                  // Reset all states and navigate to create new article
                  setIsGenerationCompleted(false);
                  setIsGenerating(false);
                  setIsDraftGenerated(false);
                  setGeneratedArticleId(null);
    setHasInitialArticle(false);
                  setArticleData(null);
                  setContentType('blogpost'); // Reset to default Blog Post
                  setArticleContext('');
                  setKeywords([]);
                  setSources([]);
                  setSelectedProject('');
                  setThumbnailSource('pexels'); // Reset to default (Stock Images)
                  setLastSaved('');
                  setSaveError('');

                  // Reset creation flags
                  isCreatingRef.current = false;
                  setIsCreatingArticle(false);

                  // Navigate to create new article (without ID, will create new one)
                  navigate('/writer/articles/create-article/step/setup?type=blogpost');
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <TopBar
        onGoBack={goBack}
        loading={isAutosaving}
        lastSaved={lastSaved}
        formatDateUpdated={formatDateUpdated}
        mode="default"
        showRightActions={false}
      />

      {/* Horizontal Stepper */}
      <WizardStepper steps={steps} currentStep={currentStep} navigateToStep={navigateToStep} />

      <div className="w-full bg-white min-h-screen article-create-sans-headings">
        <div className="mx-auto mt-8 max-w-5xl mb-32 bg-white border border-gray-200 px-8 py-10 rounded-lg">
          {saveError && <div className="text-red-500 mb-4">{saveError}</div>}

          <div className="pt-0">
            <div className="sm:flex sm:items-center mb-8">
              <div className="sm:flex-auto">
                <h1 className="text-2xl font-medium leading-6 text-neutral-900">{pageTitle}</h1>
                <p className="mt-4 text-lg text-neutral-800 opacity-80 max-w-4xl">{pageDescription}</p>
              </div>
            </div>
            {/* Main Content */}
            <div className="w-full">
              {/* Article Type Selection - Step 0 - HIDDEN (automatically set to 'single') */}

              {/* Content Setup - Step 1 (Setup) */}
              {currentStep === 1 && (
                <SetupStep
                  articleType={articleType}
                  availableStructures={availableStructures}
                  selectedStructure={selectedStructure}
                  setSelectedStructure={setSelectedStructure}
                  contentType={contentType}
                  onContentTypeChange={handleContentTypeChange}
                  includeImages={includeImages}
                  setIncludeImages={setIncludeImages}
                  thumbnailSource={thumbnailSource}
                  setThumbnailSource={setThumbnailSource}
                  showAdvancedSettings={showAdvancedSettings}
                  setShowAdvancedSettings={setShowAdvancedSettings}
                  toneOfVoice={toneOfVoice}
                  setToneOfVoice={setToneOfVoice}
                  toneOfVoiceOptions={toneOfVoiceOptions}
                  complexity={complexity}
                  setComplexity={setComplexity}
                  complexityOptions={complexityOptions}
                  language={language}
                  setLanguage={setLanguage}
                  languageOptions={languageOptions}
                />
              )}

              {/* Step 2 (Content) - Moved Article Context and Sources here */}
              {currentStep === 2 && (
                <ContentStep
                  articleType={articleType}
                  numberOfArticles={numberOfArticles}
                  setNumberOfArticles={setNumberOfArticles}
                  bulkTopic={bulkTopic}
                  setBulkTopic={setBulkTopic}
                  bulkTopicError={bulkTopicError}
                  setBulkTopicError={setBulkTopicError}
                  articleContext={articleContext}
                  articleContextError={articleContextError}
                  isDraftGenerated={isDraftGenerated}
                  editorElementRef={editorElementRef}
                  runEditorCommand={runEditorCommand}
                  convertCurrentBlock={convertCurrentBlock}
                  insertEditorBlock={insertEditorBlock}
                  contentType={contentType}
                  knowledgeMode={knowledgeMode}
                  setKnowledgeMode={setKnowledgeMode}
                  selectedArticleForSummary={selectedArticleForSummary}
                  handleSelectedArticleForSummary={handleSelectedArticleForSummary}
                  handleRemoveSelectedArticleForSummary={handleRemoveSelectedArticleForSummary}
                  triggerSourcesSearch={triggerSourcesSearch}
                  language={language}
                />
              )}

              {/* Step 3 (Sources) */}
              {currentStep === 3 && (
                <SourcesStep
                  sources={sources}
                  sourceSearchContext={sourceSearchContext}
                  keywordSearchContext={keywordSearchContext}
                  articleContext={articleContext}
                  handleToggleArticleSource={handleToggleArticleSource}
                  triggerSourcesSearch={triggerSourcesSearch}
                  language={language}
                />
              )}

              {/* Step 4 (Keywords) */}
              {currentStep === 4 && (
                <KeywordsStep
                  keywordInput={keywordInput}
                  setKeywordInput={setKeywordInput}
                  keywords={keywords}
                  setKeywords={setKeywords}
                  handleAddKeyword={handleAddKeyword}
                  handleRemoveKeyword={handleRemoveKeyword}
                  serpKeywordsVisible={serpKeywordsVisible}
                  setSerpKeywordsVisible={setSerpKeywordsVisible}
                  effectiveKeywordContext={effectiveKeywordContext}
                  triggerSerpSearch={triggerSerpSearch}
                  language={language}
                />
              )}

              {/* Step 4 (Draft Review) */}
              {currentStep === 5 && (
                <BulkReviewStep
                  articleType={articleType}
                  bulkTopic={bulkTopic}
                  numberOfArticles={numberOfArticles}
                  contentType={contentType}
                  language={language}
                  toneOfVoice={toneOfVoice}
                />
              )}

              {/* Step 4 summary (single article) */}
              {currentStep === 5 && articleType === 'single' && (
                <ReviewSummary
                  contentType={contentType}
                  selectedStructureLabel={selectedStructureLabel}
                  selectedToneLabel={selectedToneLabel}
                  selectedComplexityLabel={selectedComplexityLabel}
                  selectedLanguageLabel={selectedLanguageLabel}
                  includeImages={includeImages}
                  includeImagesLabel={includeImagesLabel}
                  thumbnailSource={thumbnailSource}
                  keywords={keywords}
                  sources={sources}
                />
              )}

            </div>
          </div>


          <WizardFooter
            currentStep={currentStep}
            articleType={articleType}
            loading={loading}
            isDraftGenerated={isDraftGenerated}
            articleData={articleData}
            numberOfArticles={numberOfArticles}
            goToPreviousStep={goToPreviousStep}
            goToNextStep={goToNextStep}
            handleSubmit={handleSubmit}
            handleContinue={handleContinue}
          />

        </div>
      </div>


      {/* Ekran generowania */}
      {isGenerating && <GeneratingOverlay />}

      {/* Ekran sukcesu */}
      {isGenerationCompleted && (
        <GenerationSuccessOverlay
          articleType={articleType}
          numberOfArticles={numberOfArticles}
          onCreateAnother={handleCreateAnother}
          goToArticles={goToArticles}
        />
      )}
    </div>
  );
}
